/**
 * Rule Triage API client — /api/v1/admin/triage.
 *
 * Surfaces the LLM-judged rule-suggestion verdicts (PRP-RULES-TRIAGE Slice 3).
 * Reuses the same Firebase auth interceptor pattern as ruleReviewApi.ts.
 */

import axios from 'axios';
import { auth } from './firebase';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[triageApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Triage request failed';
    return Promise.reject(new Error(message));
  },
);

export type Verdict =
  | 'VALID_CONDITIONAL'
  | 'VALID_CAUSAL'
  | 'WRONG_PAIRING'
  | 'PLAUSIBLE_WORKFLOW'
  | 'SPURIOUS'
  | 'MISSING_CATALOG';

export interface Guards {
  unless_present: string[];
  only_if_present: string[];
}

export interface TriageRule {
  id: string;
  name: string;
  description: string | null;
  source_corpus: string | null;
  support: number | null;
  confidence: number | null;
  status: string;
  severity: string | null;
  verdict: Verdict | null;
  verdict_confidence: number | null;
  verdict_rationale: string | null;
  verdict_evidence: string | null;
  guards: Guards | null;
  triage_stage: string | null;
  triage_filter: string | null;
  triage_meta: Record<string, unknown> | null;
}

export interface TriageSummary {
  total_proposed: number;
  by_stage: Record<string, number>;
  by_verdict: Record<string, number>;
}

export interface TriageRulesResponse {
  rules: TriageRule[];
  count: number;
  offset: number;
  limit: number;
}

export interface DecisionResult {
  rule_id: string;
  status: string;
  action: string;
  activated: boolean;
  held: boolean;
}

export const TriageAPI = {
  summary(): Promise<TriageSummary> {
    return client.get('/admin/triage/summary') as unknown as Promise<TriageSummary>;
  },
  listRules(verdict?: Verdict, limit = 200): Promise<TriageRulesResponse> {
    const params: Record<string, string | number> = { limit };
    if (verdict) params.verdict = verdict;
    return client.get('/admin/triage/rules', { params }) as unknown as Promise<TriageRulesResponse>;
  },
  decide(
    ruleId: string,
    action: 'approve' | 'reject',
    severity?: string,
  ): Promise<DecisionResult> {
    return client.post(`/admin/triage/${ruleId}/decision`, {
      action,
      ...(severity ? { severity } : {}),
    }) as unknown as Promise<DecisionResult>;
  },
};
